import { useState, useEffect } from "react";

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────
// Reads the same state the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Rich text helper ─────────────────────────────────────────────────────────
// Stores content as plain strings with a simple bold marker: **text**
// The Rich component renders them inline.

function Rich({ children }) {
  if (typeof children !== "string") return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}

// ─── Content Data (plain JS — no JSX) ────────────────────────────────────────

const sections = [
  {
    title: "1. General Information Only",
    paragraphs: [
      "All content published on Travel Rethink Ways including destination guides, travel tips, visa information, curated deals, itineraries, brand stories, and recommendations is intended for informational purposes only.",
    ],
    extraLabel: "We do not provide:",
    bullets: [
      "Legal advice",
      "Immigration advice",
      "Financial advice",
      "Professional travel consultancy",
      "Tax advice",
      "Insurance advice",
    ],
    extraParagraphs: [
      "Nothing on this website should be interpreted as professional, legal, or financial guidance. You should consult qualified professionals or official authorities before making decisions related to travel, visas, finances, or bookings.",
    ],
  },
  {
    title: "2. No Guarantee of Accuracy",
    paragraphs: [
      "While we strive to ensure that information is accurate and up to date, travel-related details can change rapidly. This includes:",
    ],
    bullets: [
      "Visa requirements",
      "Entry regulations",
      "Passport rules",
      "Airline policies",
      "Hotel policies",
      "Government travel advisories",
      "Exchange rates",
      "Travel restrictions",
      "Pricing and availability",
    ],
    extraParagraphs: [
      "We do not guarantee the completeness, reliability, or current accuracy of any information presented.",
      "Users are responsible for verifying information directly with official sources, embassies, airlines, hotels, or service providers before making travel decisions.",
    ],
  },
  {
    title: "3. Travel Risk Acknowledgment",
    paragraphs: [
      "Travel involves inherent risks, including but not limited to:",
    ],
    bullets: [
      "Flight cancellations",
      "Weather disruptions",
      "Political instability",
      "Health risks",
      "Natural disasters",
      "Visa rejections",
      "Travel delays",
    ],
    extraParagraphs: [
      "Travel Rethink Ways is not responsible for any loss, inconvenience, damage, injury, or financial impact resulting from travel decisions made based on information provided on this platform.",
      "All travel decisions are made at your own risk.",
    ],
  },
  {
    title: "4. External Links Disclaimer",
    paragraphs: [
      "Our website may contain links to third-party websites, booking platforms, travel service providers, tourism boards, or other external resources.",
    ],
    extraLabel: "We do not control, endorse, or assume responsibility for:",
    bullets: [
      "Content on external websites",
      "Accuracy of third-party information",
      "Services provided by third parties",
      "Privacy practices of external sites",
    ],
    extraParagraphs: [
      "Accessing third-party websites is done at your own discretion and risk.",
    ],
  },
  {
    title: "5. Affiliate & Sponsored Content",
    paragraphs: [
      "Some links on this website may be affiliate links. We may earn commissions if you make purchases through those links. Sponsored content may also appear on our platform.",
    ],
    extraParagraphs: [
      "Please review our **Affiliate Disclosure** for detailed information regarding partnerships and compensation.",
      "However, we do not guarantee the performance, reliability, or suitability of any product, service, hotel, airline, tour operator, or brand mentioned on this website.",
    ],
  },
  {
    title: "6. No Liability",
    paragraphs: [
      "To the fullest extent permitted by applicable law, Travel Rethink Ways disclaims all liability for any:",
    ],
    bullets: [
      "Direct loss",
      "Indirect loss",
      "Consequential damages",
      "Financial losses",
      "Personal inconvenience",
      "Missed bookings",
      "Travel disruptions",
    ],
    extraListLabel: "arising from:",
    extraList: [
      "Use of this website",
      "Reliance on information published",
      "Errors or omissions in content",
      "Decisions made based on recommendations",
    ],
    extraParagraphs: [
      "Your use of this website is entirely at your own risk.",
    ],
  },
  {
    title: "7. Editorial & Opinion Disclaimer",
    paragraphs: [
      "Certain articles, curated picks, and brand stories may reflect editorial opinions, curated recommendations, or partnership-based selections.",
    ],
    extraLabel: "Opinions expressed on this platform:",
    bullets: [
      "Are based on available information at the time of publication",
      "May not reflect universal experiences",
      "Do not constitute guaranteed outcomes",
    ],
    extraParagraphs: [
      "Travel experiences vary based on personal preferences, budgets, seasons, and other individual circumstances.",
    ],
  },
  {
    title: "8. User Responsibility",
    paragraphs: [
      "By using this website, you acknowledge that:",
    ],
    bullets: [
      "You are responsible for conducting independent research",
      "You are responsible for verifying visa requirements",
      "You are responsible for reviewing cancellation policies",
      "You are responsible for understanding travel insurance terms",
      "You are responsible for confirming booking conditions",
    ],
    extraParagraphs: [
      "Travel Rethink Ways shall not be held liable for decisions made without independent verification.",
    ],
  },
  {
    title: "9. Global Use & Jurisdiction",
    paragraphs: [
      "This website is accessible globally. However:",
    ],
    bullets: [
      "Laws and travel regulations vary by country",
      "Visa policies differ based on passport nationality",
      "Travel restrictions may apply differently to different users",
    ],
    extraParagraphs: [
      "It is your responsibility to ensure compliance with laws applicable to your country of residence and travel destination.",
    ],
  },
  {
    title: "10. Changes to This Disclaimer",
    paragraphs: [
      "We reserve the right to update, amend, or modify this Disclaimer at any time without prior notice. Changes will be reflected by updating the \"Last Updated\" date at the top of this page.",
    ],
    extraParagraphs: [
      "Continued use of the website constitutes acceptance of any revised terms.",
    ],
  },
];

// ─── Style helpers ────────────────────────────────────────────────────────────

const textPrimary = (dark) => ({ color: dark ? "#ffffff" : "rgb(13, 19, 23)" });
const textMuted   = (dark) => ({ color: dark ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" });

// ─── Component ────────────────────────────────────────────────────────────────

export default function Disclaimer() {
  const isDark = useSyncedDarkMode();
  const container = { width: "100%", maxWidth: 1500, padding: "0 24px", margin: "0 auto" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark ? "#000000" : "#ffffff",
        color: isDark ? "#ffffff" : "rgb(13, 19, 23)",
        fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        transition: "background 200ms, color 200ms",
      }}
    >
      {/* Inject fonts scoped to this component */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Yeseva+One&display=swap');
        .trw-disc-h1 {
          font-family: 'Yeseva One', Georgia, 'Times New Roman', serif !important;
          font-weight: 400 !important;
        }
      `}</style>

      <main style={{ padding: "40px 0 0" }}>

        {/* ── Hero ── */}
        <section style={container}>

          {/* Page title — Yeseva One */}
          <h1
            className="trw-disc-h1"
            style={{
              margin: 0,
              fontFamily: "'Yeseva One', Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "41px",
              lineHeight: "51px",
              letterSpacing: 0,
              ...textPrimary(isDark),
            }}
          >
            Disclaimer
          </h1>

          {/* Last Updated */}
          <p
            style={{
              margin: "14px 0 0",
              fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "29px",
              ...textPrimary(isDark),
            }}
          >
            <strong style={{ fontWeight: 600 }}>Last Updated:</strong> 19 February 2026
          </p>

          {/* Intro paragraphs */}
          <div style={{ marginTop: "20px", display: "grid", gap: "22px", marginBottom: "36px" }}>
            {[
              "The information provided on **Travel.RethinkWays.com** (\"Travel Rethink Ways,\" \"we,\" \"our,\" or \"us\") is for general informational and educational purposes only. By accessing or using this website, you agree to the terms outlined in this Disclaimer.",
              "If you do not agree with this Disclaimer, please discontinue use of the website.",
            ].map((line, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  fontSize: "17px",
                  lineHeight: "30px",
                  ...textMuted(isDark),
                }}
              >
                <Rich>{line}</Rich>
              </p>
            ))}
          </div>
        </section>

        {/* ── Article Sections ── */}
        <article style={container}>
          {sections.map((section, idx) => (
            <section
              key={idx}
              style={{
                borderTop: 0,
                marginTop: idx === 0 ? 0 : "36px",
              }}
            >
              {/* Section heading */}
              {section.title && (
                <h2
                  style={{
                    margin: "0 0 12px",
                    fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: "28px",
                    lineHeight: "39px",
                    ...textPrimary(isDark),
                  }}
                >
                  {section.title}
                </h2>
              )}

              {/* Paragraphs */}
              {section.paragraphs && section.paragraphs.map((para, i) => (
                <p
                  key={i}
                  style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Extra label (before bullets) */}
              {section.extraLabel && (
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{section.extraLabel}</Rich>
                </p>
              )}

              {/* Bullet list */}
              {section.bullets && section.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{bullet}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra list label (second list intro) */}
              {section.extraListLabel && (
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{section.extraListLabel}</Rich>
                </p>
              )}

              {/* Extra list (second bullet list) */}
              {section.extraList && section.extraList.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.extraList.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{item}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra paragraphs */}
              {section.extraParagraphs && section.extraParagraphs.map((para, i) => (
                <p
                  key={i}
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Contact lines */}
              {section.contactLines && section.contactLines.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {section.contactLines.map((line, i) => (
                    <p
                      key={i}
                      style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                    >
                      <strong style={{ fontWeight: 600, ...textPrimary(isDark) }}>
                        {line.label}:
                      </strong>{" "}
                      {line.href ? (
                        <a
                          href={line.href}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            ...textPrimary(isDark),
                          }}
                        >
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}
